import type { ReactNode } from 'react'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { SearchField } from '../../platform/primitives/SearchField'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import type { CategoryView } from './useCategoryView'
import './categories.css'

/**
 * The categories toolbar — filter, sort order, expand-all, and the page's
 * primary action.
 *
 * A SIBLING of the panel, not a child of it, and rendered unconditionally by
 * the route. It used to live inside `CategoryTable`, which renders only in
 * `<Async>`'s success branch: the filter, the sort and "New category" all
 * vanished while the list was loading and on an error. `useCategoryView` holds
 * the state now, so this row survives every Async state — the same shape
 * `MerchantsToolbar` already had.
 *
 * `action` is a NODE the route supplies rather than a button built here: the
 * entitlement gate around it is the route's business (M11-R18 wants it firing
 * at the point of intent), while the row it sits in is this component's
 * layout.
 */
export function CategoriesToolbar({ view, action }: { view: CategoryView; action?: ReactNode }) {
  const { query, setQuery, sort, setSort, sorts, expandable, allOpen, toggleAll } = view
  const target = useHitTarget('page')

  return (
<div className="categories__toolbar">
        <SearchField
          className="categories__search"
          label="Filter categories"
          placeholder="Filter categories"
          value={query}
          onChange={setQuery}
        />
  
        <SegmentedControl
          label="Sort order"
          name="categories-sort"
          options={sorts.map((s) => ({ value: s.key, label: s.label }))}
          value={sort}
          onChange={setSort}
        />
  
        {/* Absent when nothing can expand, rather than disabled — the same
            rule the Manage capsule follows on a system row, and it keeps this
            sheet out of the disabled-skin business. */}
        {expandable.length > 0 && (
          <button
            type="button"
            className="categories__expand-all hoverable"
            style={{ minHeight: target }}
            onClick={toggleAll}
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
        {action !== undefined && <span className="categories__action">{action}</span>}
      </div>
  )
}
